import { useMemo, useRef } from "react";
import { Download, Eye, FileText, RefreshCw, Upload, X } from "lucide-react";
import ToastHelper from "@/utils/toastHelper";
import { STUDENT_DOCUMENT_ACCEPT, STUDENT_DOCUMENT_MAX_BYTES } from "@/constants/studentDocuments";
import { studentDocumentsService } from "@/services/studentDocumentsService";

const DEFAULT_DOCUMENT_TYPES = [
  {
    studentDocTypeId: 1,
    key: "photo",
    label: "Passport-style photo",
    optional: false,
  },
  {
    studentDocTypeId: 2,
    key: "licence",
    label: "Driver Licence (front + back)",
    optional: false,
  },
  {
    studentDocTypeId: 3,
    key: "passport",
    label: "Passport (optional)",
    optional: true,
  },
  {
    studentDocTypeId: 4,
    key: "usi",
    label: "USI supporting document",
    optional: false,
  },
];

const normaliseDocuments = (documents = []) =>
  DEFAULT_DOCUMENT_TYPES.map((documentType) => {
    const existing = documents.find(
      (document) =>
        Number(
          document?.studentDocTypeId ??
            document?.documentTypeId ??
            document?.StudentDocTypeId ??
            document?.DocumentTypeId
        ) === documentType.studentDocTypeId
    );

    return {
      ...documentType,
      ...(existing || {}),
      studentDocTypeId: documentType.studentDocTypeId,
      key: documentType.key,
      label: documentType.label,
      optional: documentType.optional,
    };
  });

export default function StudentEnrollmentDocuments({
  documents = [],
  setDocuments,
  onDocumentsChange,
  readOnly = false,
  onViewDocument,
  onDownloadDocument,
}) {
  const inputRefs = useRef({});
  const documentList = useMemo(() => normaliseDocuments(documents), [documents]);

  const updateDocuments = (nextDocuments) => {
    if (typeof onDocumentsChange === "function") {
      onDocumentsChange(nextDocuments);
      return;
    }

    if (typeof setDocuments === "function") {
      setDocuments(nextDocuments);
    }
  };

  const chooseFile = (studentDocTypeId) => {
    inputRefs.current[studentDocTypeId]?.click();
  };

  const handleFileSelected = (studentDocTypeId, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    if (file.size > STUDENT_DOCUMENT_MAX_BYTES) {
      ToastHelper.error("The selected file must be 10 MB or smaller.");
      return;
    }

    const nextDocuments = documentList.map((document) =>
      document.studentDocTypeId === studentDocTypeId
        ? {
            ...document,
            file,
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
            isPendingUpload: true,
          }
        : document
    );

    updateDocuments(nextDocuments);
    ToastHelper.success(`${file.name} selected`);
  };

  const removeSelectedFile = (studentDocTypeId) => {
    const nextDocuments = documentList.map((document) =>
      document.studentDocTypeId === studentDocTypeId
        ? {
            ...document,
            file: null,
            fileName: document.studentDocId ? document.fileName : "",
            fileType: document.studentDocId ? document.fileType : "",
            fileSize: document.studentDocId ? document.fileSize : 0,
            isPendingUpload: false,
          }
        : document
    );

    updateDocuments(nextDocuments);
  };

  const openLocalFile = (document, download = false) => {
    if (!document.file || typeof URL === "undefined") return false;
    const objectUrl = URL.createObjectURL(document.file);
    if (download) {
      const link = window.document.createElement("a");
      link.href = objectUrl;
      link.download = document.fileName || document.file.name;
      link.click();
    } else {
      window.open(objectUrl, "_blank", "noopener,noreferrer");
    }
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    return true;
  };

  const openBlob = (response, document, download) => {
    const blob = response?.data instanceof Blob ? response.data : new Blob([response?.data]);
    const objectUrl = URL.createObjectURL(blob);
    if (download) {
      const link = window.document.createElement("a");
      link.href = objectUrl;
      link.download = document.fileName || "document";
      link.click();
    } else {
      window.open(objectUrl, "_blank", "noopener,noreferrer");
    }
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  };

  const viewDocument = async (document) => {
    try {
      if (openLocalFile(document, false)) return;
      if (typeof onViewDocument === "function") {
        openBlob(await onViewDocument(document), document, false);
        return;
      }
      if (document.studentDocId) {
        openBlob(await studentDocumentsService.viewEnrollmentDocument(document.studentDocId), document, false);
        return;
      }
      if (document.downloadUrl) {
        window.open(document.downloadUrl, "_blank", "noopener,noreferrer");
        return;
      }
      ToastHelper.error("This document is not available to view.");
    } catch (error) {
      ToastHelper.apiError(error, "Unable to view this document.");
    }
  };

  const downloadDocument = async (document) => {
    try {
      if (openLocalFile(document, true)) return;
      if (typeof onDownloadDocument === "function") {
        openBlob(await onDownloadDocument(document), document, true);
        return;
      }
      if (document.studentDocId) {
        openBlob(await studentDocumentsService.downloadEnrollmentDocument(document.studentDocId), document, true);
        return;
      }
      if (document.downloadUrl) {
        const link = window.document.createElement("a");
        link.href = document.downloadUrl;
        link.download = document.fileName || "document";
        link.click();
        return;
      }
      ToastHelper.error("This document is not available for download.");
    } catch (error) {
      ToastHelper.apiError(error, "Unable to download this document.");
    }
  };

  return (
    <div data-testid="student-enrollment-documents">
      {!readOnly ? (
        <p className="mb-4 text-sm text-slate-500">
          Fields marked with * are required. Documents will be uploaded when you save
          or submit the enrolment.
        </p>
      ) : null}

      <div className="space-y-3">
        {documentList.map((document) => {
          const hasDocument = Boolean(document.file || document.fileName);
          const isPending = Boolean(document.file);

          return (
            <div
              key={document.studentDocTypeId}
              className="flex min-h-[76px] items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 transition-colors hover:border-slate-300"
              data-testid={`document-row-${document.key}`}
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-400">
                <FileText className="h-5 w-5" />
              </div>

              <div className="min-w-0 flex-1">
                <div className="text-base font-semibold text-[#0A2540]">
                  {document.label}{!document.optional ? " *" : ""}
                </div>

                <div
                  className={`mt-0.5 truncate text-xs ${
                    hasDocument ? "text-emerald-700" : "text-slate-400"
                  }`}
                  title={document.fileName || "Not yet selected"}
                >
                  {hasDocument
                    ? `${document.fileName}${isPending ? " — ready to upload" : ""}`
                    : "Not yet selected"}
                </div>
              </div>

              <input
                ref={(element) => {
                  inputRefs.current[document.studentDocTypeId] = element;
                }}
                type="file"
                accept={STUDENT_DOCUMENT_ACCEPT}
                className="hidden"
                onChange={(event) =>
                  handleFileSelected(document.studentDocTypeId, event)
                }
                data-testid={`document-input-${document.key}`}
              />

              <div className="flex shrink-0 items-center gap-2">
                {hasDocument && (
                  <>
                    <button
                      type="button"
                      onClick={() => viewDocument(document)}
                      className="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                      data-testid={`document-view-${document.key}`}
                    >
                      <Eye className="h-4 w-4" />
                      <span className="hidden sm:inline">View</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => downloadDocument(document)}
                      className="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                      data-testid={`document-download-${document.key}`}
                    >
                      <Download className="h-4 w-4" />
                      <span className="hidden sm:inline">Download</span>
                    </button>
                  </>
                )}

                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => chooseFile(document.studentDocTypeId)}
                    className="inline-flex h-10 min-w-[110px] items-center justify-center gap-1.5 rounded-lg bg-[#F4510B] px-4 text-sm font-semibold text-white transition-colors hover:bg-[#D94808] focus:outline-none focus:ring-2 focus:ring-orange-300 focus:ring-offset-2"
                    data-testid={`document-upload-${document.key}`}
                  >
                    {hasDocument ? (
                      <RefreshCw className="h-4 w-4" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    {hasDocument ? "Replace" : "Upload"}
                  </button>
                )}

                {!readOnly && isPending && (
                  <button
                    type="button"
                    onClick={() => removeSelectedFile(document.studentDocTypeId)}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-rose-200 text-rose-600 transition-colors hover:bg-rose-50"
                    aria-label={`Remove ${document.label}`}
                    title="Remove selected file"
                    data-testid={`document-remove-${document.key}`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!readOnly ? (
        <p className="mt-3 text-xs text-slate-400">
          Accepted formats: PDF, JPG and PNG. Maximum file size: 10 MB.
        </p>
      ) : null}
    </div>
  );
}
